import { styled } from "goober";
import type { ReactNode } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";
import { Icon, type IconName } from "../Icon";
import { Tooltip } from "../Tooltip";
import { ChatpadButton } from "./ChatpadButton";
import { ChatpadToggleButton } from "./ChatpadToggleButton";

export type ChatpadSection = "outputs" | "sources";
export const chatpadRailWidth = 40;

const Frame = styled("div")`
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
`;

const Rail = styled("nav")`
  display: flex;
  flex-direction: column;
  width: ${chatpadRailWidth}px;
  flex-shrink: 0;
  overflow-y: auto;
  border-right: 0.5px solid var(--border-alpha);

  &[data-collapsed="true"] {
    border-right: none;
  }
`;

const Sections = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  flex-shrink: 0;
`;

const ToggleSlot = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: auto;
  padding: 8px 0;
  flex-shrink: 0;
`;

const Resources = styled("div")`
  --cell-bg: var(--surface-secondary);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin: 0 8px;
  padding: 8px 0;
  border-top: 0.5px solid var(--border-alpha);
  flex-shrink: 0;
`;

const RailButton = styled(ChatpadButton)`
  color: var(--text-secondary);

  &[aria-pressed="true"] {
    color: var(--text-primary);
    background: var(--alpha8);
  }
  &[aria-pressed="false"] > * {
    opacity: 1;
  }
`;

const Content = styled("div")`
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`;

const sections: { id: ChatpadSection; label: string; icon: IconName }[] = [
  { id: "outputs", label: "Work", icon: "tool-case" },
  { id: "sources", label: "Sources", icon: "earth" },
];

export function Chatpad({
  open,
  onToggle,
  activeSection,
  onSelectSection,
  resources,
  actions,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  activeSection: ChatpadSection;
  onSelectSection: (section: ChatpadSection) => void;
  resources?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const isMobile = useIsMobile();
  return (
    <Frame>
      <Rail aria-label="Side panel navigation" data-collapsed={!open}>
        <Sections>
          {sections.map((section) => (
            <Tooltip
              key={section.id}
              content={section.label}
              side={isMobile ? "right" : "left"}
            >
              <RailButton
                aria-label={section.label}
                aria-pressed={activeSection === section.id}
                onPress={() => onSelectSection(section.id)}
              >
                <Icon name={section.icon} size={12} />
              </RailButton>
            </Tooltip>
          ))}
        </Sections>
        {resources && <Resources>{resources}</Resources>}
        <ToggleSlot>
          {actions}
          <ChatpadToggleButton isOpen={open} onToggle={onToggle} />
        </ToggleSlot>
      </Rail>
      {open && <Content>{children}</Content>}
    </Frame>
  );
}
